import React from 'react';

/**
 * Inline SVG icon set. These cover the small UI glyphs (chevrons, contact
 * details, card marks) that the design uses at sizes where the exported PNG
 * artwork would be wasteful; the large pillar and vision artwork stays as
 * images in `public/assets`.
 */

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const paths = {
  menu: <path {...stroke} d="M3 6h18M3 12h18M3 18h18" />,
  close: <path {...stroke} d="M6 6l12 12M18 6L6 18" />,
  play: <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />,
  check: <path {...stroke} d="M20 6L9 17l-5-5" />,
  arrowRight: <path {...stroke} d="M4 12h16m0 0l-6-6m6 6l-6 6" />,

  users: (
    <g {...stroke}>
      <circle cx="9" cy="7" r="4" />
      <path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" />
      <path d="M16 3.1a4 4 0 0 1 0 7.8" />
      <path d="M19 15.1A4 4 0 0 1 22 19v2" />
    </g>
  ),
  book: (
    <g {...stroke}>
      <path d="M3 17.5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v12.5a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
      <path d="M12 7v13.5" />
    </g>
  ),
  briefcase: (
    <g {...stroke}>
      <rect x="2" y="6.5" width="20" height="13" rx="2" />
      <path d="M16 19.5V4.5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v15" />
    </g>
  ),
  // Lucide's "hand-helping": an open hand offering support.
  'helping-hand': (
    <g {...stroke}>
      <path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14" />
      <path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
      <path d="m2 13 6 6" />
    </g>
  ),
  // Lucide's "hand-heart": an open hand cradling a heart. Chosen over a
  // hand-drawn path because it stays legible down to ~20px.
  'hand-heart': (
    <g {...stroke}>
      <path d="M11 14h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 16" />
      <path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
      <path d="m2 15 6 6" />
      <path d="M19.5 8.5c.7-.7 1.5-1.6 1.5-2.7A2.73 2.73 0 0 0 16 4a2.78 2.78 0 0 0-5 1.8c0 1.2.8 2 1.5 2.8L16 12Z" />
    </g>
  ),
  heart: (
    <path
      d="M12 20.4l-1.3-1.2C6.1 15.1 3.2 12.5 3.2 9.2A4.7 4.7 0 0 1 8 4.5c1.5 0 3 .7 4 1.9 1-1.2 2.5-1.9 4-1.9a4.7 4.7 0 0 1 4.8 4.7c0 3.3-2.9 5.9-7.5 10z"
      fill="currentColor"
    />
  ),
  // Lucide's "handshake" — two clasped hands. Same reasoning as heart-hand.
  handshake: (
    <g {...stroke}>
      <path d="m11 17 2 2a1 1 0 1 0 3-3" />
      <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
      <path d="m21 3 1 11h-2" />
      <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
      <path d="M3 4h8" />
    </g>
  ),
  // Two palms pressed together, fingertips up, thumbs folded at the wrists.
  // The centre seam is what stops the silhouette reading as a single leaf.
  pray: (
    <g {...stroke}>
      <path d="M12 3.4C10.4 5.6 8.6 8.4 7.9 10.4a5 5 0 0 0-.3 1.8v5a2.4 2.4 0 0 0 1.4 2.2l3 1.2" />
      <path d="M12 3.4c1.6 2.2 3.4 5 4.1 7a5 5 0 0 1 .3 1.8v5a2.4 2.4 0 0 1-1.4 2.2l-3 1.2" />
      <path d="M12 3.4v17.2" />
      <path d="m7.6 13.2-2 1.4a2 2 0 0 0-.6 2.6l1.2 2" />
      <path d="m16.4 13.2 2 1.4a2 2 0 0 1 .6 2.6l-1.2 2" />
    </g>
  ),

  mail: (
    <g {...stroke}>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M3 6.5l9 6.2 9-6.2" />
    </g>
  ),
  phone: (
    <path
      {...stroke}
      d="M6.2 3.5h2.6l1.4 3.6-1.9 1.3a11.4 11.4 0 0 0 5.3 5.3l1.3-1.9 3.6 1.4v2.6a2 2 0 0 1-2.2 2A16.3 16.3 0 0 1 4.2 5.7a2 2 0 0 1 2-2.2z"
    />
  ),
  pin: (
    <g {...stroke}>
      <path d="M12 21.2s6.6-5.5 6.6-10.4a6.6 6.6 0 1 0-13.2 0C5.4 15.7 12 21.2 12 21.2z" />
      <circle cx="12" cy="10.6" r="2.4" />
    </g>
  ),

  quote: (
    <path
      d="M9.6 5.4c-3.5 1.5-5.4 4.2-5.4 8v5.2h6.6v-6.4H7.5c.2-1.9 1.2-3.2 3.1-4zM20.4 5.4c-3.5 1.5-5.4 4.2-5.4 8v5.2h6.6v-6.4h-3.3c.2-1.9 1.2-3.2 3.1-4z"
      fill="currentColor"
    />
  ),
};

export default function Icon({ name, className = 'h-6 w-6', ...rest }) {
  const path = paths[name];
  if (!path) return null;

  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false" {...rest}>
      {path}
    </svg>
  );
}
